import i18n from '../i18n'
import { errorCode } from '../services/api'
import { faDigits } from './numerals'
import { isoDate, parseIsoDate, toGregorian, toJalali } from './jalali'

/**
 * The profile-completion form's rules (design page 14, `editProfile`),
 * mirrored from `EditProfileScreen.kt` in the Android app so both clients
 * count the same three items, allow the same years, and send the same body.
 */

const t = (key, options) => i18n.t(key, { ns: 'editProfile', ...options })

/** The three items the completion meter counts; the avatar always counts as done. */
export const PROFILE_ITEMS = 3
export const BIRTH_YEAR_MIN = 1320
/** 1405 is the year of today's calendar (2026), as in the Android form's `BIRTH_YEAR_MAX`. */
export const BIRTH_YEAR_MAX = 1405
/** How long after the last keystroke the server is asked about a display name. */
export const NAME_CHECK_DELAY_MS = 400

/** The Jalali month names — a function, not a constant, since the active language decides which array comes back. */
export const persianMonths = () => t('months', { returnObjects: true })

/** 33 / 67 / 100 — the design's meter, with the avatar always the first third. */
export function profileCompletion(fullName, displayName) {
  const done = 1 + (fullName.trim() ? 1 : 0) + (displayName.trim() ? 1 : 0)
  return Math.round((done * 100) / PROFILE_ITEMS)
}

/**
 * The form's initial values from the server's profile (`MeProfileDto`,
 * B3). The Gregorian birth date becomes the Jalali day / month / year the
 * steppers show, exactly as stored — the form's year range limits what the
 * steppers can *reach*, never what was already saved. `birthTouched` is
 * false until a stepper moves; only then does a birthday go on the wire.
 */
export function formFromProfile(user) {
  const parsed = user?.birthDate ? parseIsoDate(user.birthDate) : null
  const birth = parsed ? toJalali(parsed.year, parsed.month, parsed.day) : null
  return {
    fullName: user?.fullName ?? '',
    displayName: user?.displayName ?? '',
    day: birth?.day ?? 1,
    month: birth?.month ?? 1,
    year: birth?.year ?? 1370,
    birthSet: birth !== null,
    birthTouched: false,
  }
}

/**
 * One tap of the year stepper (+1 / -1), kept inside [BIRTH_YEAR_MIN, BIRTH_YEAR_MAX].
 * A stored year already outside the range only ever moves toward it, so
 * the first tap never jumps across the range.
 */
export function stepYear(year, direction) {
  return direction > 0 ? Math.max(year, Math.min(BIRTH_YEAR_MAX, year + 1)) : Math.min(year, Math.max(BIRTH_YEAR_MIN, year - 1))
}

/** The form's Gregorian `YYYY-MM-DD` for the wire, or null when the date was never set. */
export function wireBirthDate(form) {
  return form.birthSet ? isoDate(toGregorian(form.year, form.month, form.day)) : null
}

/**
 * Only what changed goes on the wire (`UpdateProfileDto`: absent fields
 * are left alone). Re-sending an unchanged display name would be a no-op
 * on the server anyway, but not sending it keeps a locked name from
 * tripping the 30-day rule when only the birthday moved.
 */
export function updateBody(form, user) {
  const body = {}
  const fullName = form.fullName.trim()
  if (fullName !== (user?.fullName ?? '')) body.fullName = fullName
  const birthDate = wireBirthDate(form)
  // A stored birthday that no stepper has touched is never sent back (it would be rewritten through the form's own year range).
  if (form.birthTouched && birthDate !== null && birthDate !== (user?.birthDate ?? null)) body.birthDate = birthDate
  const displayName = form.displayName.trim()
  if (displayName && displayName !== (user?.displayName ?? '')) body.displayName = displayName
  return body
}

/**
 * The server's error code, or the transport, as the form's vocabulary
 * (docs/api-contract.md §5, B3). Anything the form has no sentence for is
 * a service failure — retrying the same save might still work.
 */
export function updateFailureOf(error) {
  if (!error?.response) return 'offline'
  switch (errorCode(error)) {
    case 'display_name_taken':
      return 'name_taken'
    case 'display_name_locked':
      return 'name_locked'
    case 'display_name_invalid':
      return 'name_invalid'
    case 'birth_date_invalid':
      return 'date_invalid'
    default:
      return error.response.status === 409 ? 'name_taken' : 'service'
  }
}

/** An ISO instant's calendar day as «۱۵ مهر ۱۴۰۵», for the lock notice. */
export function jalaliDayLabel(isoInstant) {
  const date = parseIsoDate(String(isoInstant ?? '').slice(0, 10))
  if (!date) return null
  const j = toJalali(date.year, date.month, date.day)
  return `${faDigits(j.day)} ${persianMonths()[j.month - 1]} ${faDigits(j.year)}`
}

/** The badge under the display-name field: what the server said, or what is known without asking. */
export function nameBadge(blank, check) {
  if (blank) return { label: t('badge.empty'), tone: 'muted' }
  if (!check) return { label: t('badge.checking'), tone: 'muted' }
  if (check.reason === 'own') return { label: t('badge.own'), tone: 'muted' }
  if (check.reason === 'taken') return { label: t('badge.taken'), tone: 'bad' }
  if (check.reason === 'invalid') return { label: t('badge.invalid'), tone: 'warn' }
  return check.available ? { label: t('badge.available'), tone: 'good' } : { label: t('badge.taken'), tone: 'bad' }
}
